import React, { useEffect, useState } from "react";
import api from "../api/api";

import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";

import {
    LockReset,
    Visibility,
    VisibilityOff,
} from "@mui/icons-material";

const ORANGE = "#ff6b00";
const BLACK = "#111111";
const CHAMELEON = "#b7e000";

const ChangeUserPassword = ({ onClose }) => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState("");

    const [loadingUsers, setLoadingUsers] = useState(true);
    const [resetting, setResetting] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoadingUsers(true);

                const token = localStorage.getItem("access");

                const response = await api.get(
                    "/api/users/users-users/",
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || [];

                setUsers(data);
            } catch (err) {
                console.error("Failed to load users:", err);
                setError("Unable to load users.");
            } finally {
                setLoadingUsers(false);
            }
        };

        fetchUsers();
    }, []);

    const handleReset = async () => {
        setMessage("");
        setError("");

        if (!selectedUser) {
            setError("Please select a username.");
            return;
        }

        if (!newPassword.trim()) {
            setError("Please enter a new password.");
            return;
        }

        if (newPassword.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }

        try {
            setResetting(true);

            const token = localStorage.getItem("access");

            await api.post(
                "/api/users/admin-reset-password/",
                {
                    user_id: selectedUser.id,
                    new_password: newPassword,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setMessage("Password reset successfully.");
            setNewPassword("");

            setTimeout(() => {
                if (onClose) {
                    onClose();
                }
            }, 1000);
        } catch (err) {
            console.error("Password reset failed:", err);

            setError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Failed to reset password."
            );
        } finally {
            setResetting(false);
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                background: "#fff",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${BLACK}, #292929)`,
                    px: 2.5,
                    py: 1.6,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                }}
            >
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "10px",
                        background: ORANGE,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <LockReset
                        sx={{
                            color: "#fff",
                            fontSize: 22,
                        }}
                    />
                </Box>

                <Typography
                    sx={{
                        color: "#fff",
                        fontSize: "17px",
                        fontWeight: 700,
                    }}
                >
                    Reset User Password
                </Typography>
            </Box>

            {/* Form */}
            <Box
                sx={{
                    p: 2.5,
                }}
            >
                <Autocomplete
                    options={users}
                    value={selectedUser}
                    onChange={(event, value) => {
                        setSelectedUser(value);
                        setError("");
                        setMessage("");
                    }}
                    loading={loadingUsers}
                    getOptionLabel={(option) =>
                        option?.username ||
                        option?.email ||
                        option?.name ||
                        ""
                    }
                    isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Username"
                            size="small"
                            autoComplete="off"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingUsers ? (
                                            <CircularProgress
                                                size={18}
                                                sx={{
                                                    color: ORANGE,
                                                }}
                                            />
                                        ) : null}

                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={{
                                mb: 1.8,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                        borderColor: ORANGE,
                                    },
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: ORANGE,
                                },
                            }}
                        />
                    )}
                />

                <TextField
                    fullWidth
                    size="small"
                    type={showPassword ? "text" : "password"}
                    label="New Password"
                    value={newPassword}
                    onChange={(e) => {
                        setNewPassword(e.target.value);
                        setError("");
                        setMessage("");
                    }}
                    autoComplete="new-password"
                    sx={{
                        mb: 1.5,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                                borderColor: ORANGE,
                            },
                        "& .MuiInputLabel-root.Mui-focused": {
                            color: ORANGE,
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setShowPassword((prev) => !prev)
                                    }
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 1.5,
                            py: 0,
                            borderRadius: "7px",
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {message && (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 1.5,
                            py: 0,
                            borderRadius: "7px",
                        }}
                    >
                        {message}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 0.5,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={resetting}
                        sx={{
                            borderColor: "#ccc",
                            color: "#555",
                            textTransform: "none",
                            borderRadius: "7px",
                            px: 2,
                            "&:hover": {
                                borderColor: "#999",
                                background: "#f5f5f5",
                            },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleReset}
                        disabled={resetting}
                        sx={{
                            background: ORANGE,
                            color: "#fff",
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: "7px",
                            px: 2.2,
                            "&:hover": {
                                background: "#e55f00",
                            },
                        }}
                    >
                        {resetting ? (
                            <CircularProgress
                                size={19}
                                sx={{
                                    color: "#fff",
                                }}
                            />
                        ) : (
                            "Reset Password"
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ChangeUserPassword;